import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { CONTACTS, WA_DEFAULT } from "@/data/content";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/smooth";

const NAV = [
  { label: "Offerte", to: "/offerte/" },
  { label: "NLT Usato", to: "/usato/" },
  { label: "Partita IVA", to: "/noleggio-lungo-termine-partita-iva/" },
  { label: "Aziende", to: "/noleggio-lungo-termine-aziende/" },
  { label: "Privati", to: "/noleggio-lungo-termine-privati/" },
  { label: "Guide", to: "/guide/" },
  { label: "Chi Siamo", to: "/chi-siamo/" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goAnchor = (anchor) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(anchor), 350);
    } else {
      scrollToId(anchor);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-xmove-ink/8 bg-xmove-cream" data-testid="header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" aria-label="XMOVE Italia — xmoveitalia.com — Home" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {NAV.map((item) =>
            item.anchor ? (
              <button
                key={item.label}
                onClick={() => goAnchor(item.anchor)}
                data-testid={`nav-${item.anchor}`}
                className="text-sm font-medium text-xmove-ink/80 transition-colors hover:text-xmove-orange"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-xmove-ink/80 transition-colors hover:text-xmove-orange"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={CONTACTS.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 rounded-full border border-xmove-ink/15 px-4 py-2 text-sm font-semibold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange"
          >
            <Phone size={15} />
            {CONTACTS.phoneDisplay}
          </a>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="flex items-center gap-2 rounded-full bg-xmove-orange px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all hover:bg-xmove-orangeDark hover:shadow-lift"
          >
            <MessageCircle size={16} />
            WhatsApp
          </a>
        </div>

        <button
          className="lg:hidden text-xmove-ink"
          onClick={() => setOpen(!open)}
          data-testid="header-mobile-menu-btn"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-xmove-ink/8 bg-xmove-cream px-5 py-6 lg:hidden" data-testid="header-mobile-menu">
          <nav className="flex flex-col gap-4">
            {NAV.map((item) =>
              item.anchor ? (
                <button
                  key={item.label}
                  onClick={() => goAnchor(item.anchor)}
                  data-testid={`mobile-nav-${item.anchor}`}
                  className="text-left font-display text-lg font-semibold text-xmove-ink"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-display text-lg font-semibold text-xmove-ink"
                >
                  {item.label}
                </Link>
              )
            )}
            <a href={CONTACTS.phoneHref} data-testid="mobile-phone-link" className="mt-2 flex items-center gap-2 text-sm font-semibold text-xmove-ink">
              <Phone size={16} className="text-xmove-orange" /> {CONTACTS.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
